/* eslint-disable react/display-name */
import { useMemo } from 'react';
import { QueryParamConfig, decodeObject, encodeObject } from 'use-query-params';
import { format, parse } from 'date-fns';
import { useQueryParam, withDefault } from 'use-query-params';

import { removeNullAndUndefined } from 'utils/object';
import { DATE_FILTER_TYPE, DateFilterValue } from './date-filter-data';
import { DateFilterButton as DateFilterElement, IFilterButtonProps } from './date-filter-button';

export const DATE_FILTER_QUERY_KEY = 'datum';

export type UseDateFilterButtonReturn = Pick<IFilterButtonProps, 'dateFilter' | 'setDateFilter'> & {
	DateFilterButton: (props: IFilterButtonProps) => React.JSX.Element;
};

export const useDateFilterButton = (): UseDateFilterButtonReturn => {
	const [dateFilter, setDateFilter] = useQueryParam<DateFilterValue | null>(
		DATE_FILTER_QUERY_KEY,
		withDefault(DateFilterParam, null)
	);

	const DateFilterButtonElement = useMemo(
		() =>
			function (props: IFilterButtonProps) {
				return <DateFilterElement {...props} dateFilter={dateFilter} setDateFilter={setDateFilter} useMobilePicker />;
			},
		[dateFilter?.type, dateFilter?.startDate, dateFilter?.endDate]
	);

	return {
		dateFilter,
		setDateFilter,
		DateFilterButton: DateFilterButtonElement
	};
};

export const DateFilterParam: QueryParamConfig<DateFilterValue | undefined | null, DateFilterValue | undefined | null> =
	{
		encode: (value) => {
			if (!value) return value;

			if (value.type === DATE_FILTER_TYPE.ALL) return null;

			const startDate = value?.startDate ? format(new Date(value.startDate), 'dd-MM-yyyy') : undefined;
			const endDate = value?.endDate ? format(new Date(value.endDate), 'dd-MM-yyyy') : undefined;

			return encodeObject(
				removeNullAndUndefined({
					startDate,
					endDate,
					type: value?.type
				})
			);
		},
		decode: (value): DateFilterValue | undefined => {
			const decodedValue = decodeObject(value);

			if (decodedValue) {
				return {
					startDate: decodedValue.startDate ? parse(decodedValue.startDate, 'dd-MM-yyyy', new Date()) : undefined,
					endDate: decodedValue.endDate ? parse(decodedValue.endDate, 'dd-MM-yyyy', new Date()) : undefined,
					type: decodedValue.type as DateFilterValue['type']
				};
			}

			return undefined;
		}
	};
